// @ai-generated 2026-09-15T09:59:29+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
import { type ApiContainerAction } from '@Model';
import { ContainerActionTag, type ContainerActionListLine, toContainerActionListLines } from '@Modules/Orders';
import { FrameEditContainerType } from '@Modules/Stammdaten';
import { LinkedCell, UnlinkedCell, useOnTheFlyModal } from '@synqo/frontend-component-library';
import { type FC, memo, useCallback, useMemo, useRef } from 'react';
import styles from './LinkedContainerActionsCell.module.css';

export interface LinkedContainerActionsCellProps {
    containerActions: readonly ApiContainerAction[];
}

interface ContainerActionItemProps {
    line: ContainerActionListLine;
    onOpenContainerTypeModal: (containerTypeId: string) => void;
}

const ContainerActionItem: FC<ContainerActionItemProps> = memo(function ContainerActionItem({
    line,
    onOpenContainerTypeModal,
}: ContainerActionItemProps) {
    const { typeId, action, label, quantity } = line;

    const handleClick = useCallback(() => {
        onOpenContainerTypeModal(typeId);
    }, [typeId, onOpenContainerTypeModal]);

    return (
        <span className={styles.entry}>
            <ContainerActionTag action={action} />

            <span className={styles.quantity}>{quantity > 1 && `${quantity} ×`}</span>

            {/* A staged order may name a type the master data does not know yet; that one has nothing to open. */}
            {typeId ? (
                <LinkedCell label={label} itemId={typeId} onClick={handleClick} />
            ) : (
                <UnlinkedCell label={label} />
            )}
        </span>
    );
});

/**
 * The container actions of an order as one table cell: what is delivered and collected, by type.
 *
 * Mirrors the service column beside it — one line per entry, the type opening its master data record —
 * because both columns are read at a glance while scanning the list. An exchange is one line.
 */
export const LinkedContainerActionsCell: FC<LinkedContainerActionsCellProps> = memo(
    function LinkedContainerActionsCell({ containerActions }: LinkedContainerActionsCellProps) {
        const modalIdRef = useRef('');
        const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

        const closeModal = useCallback(() => {
            if (modalIdRef.current) {
                closeOnTheFlyModals(modalIdRef.current);
                modalIdRef.current = '';
            }
        }, [closeOnTheFlyModals]);

        const handleOpenContainerTypeModal = useCallback(
            (containerTypeId: string) => {
                modalIdRef.current = openOnTheFlyModal({
                    child: (
                        <FrameEditContainerType
                            primaryKey={containerTypeId}
                            canEdit={false}
                            canCreate={false}
                            canDelete={false}
                            events={{
                                onClose: closeModal,
                            }}
                        />
                    ),
                });
            },
            [openOnTheFlyModal, closeModal],
        );

        const lines = useMemo(() => toContainerActionListLines(containerActions), [containerActions]);

        return (
            <div className={styles.lines}>
                {lines.map((line) => (
                    <ContainerActionItem
                        key={line.key}
                        line={line}
                        onOpenContainerTypeModal={handleOpenContainerTypeModal}
                    />
                ))}
            </div>
        );
    },
);
